import { useState, useEffect, useCallback, useRef } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { toast } from 'sonner'
import type { ChatThread, ChatMessage } from '../types'
import { threadSearchResultToChatThread } from '../utils/thread-search-result-to-chat-thread'
import { createCompositeId, extractThreadId } from '../utils/composite-id'
import {
  getThreadsClient,
  type ThreadWithDetails,
} from '../../../lib/api/threads.client.js'
import { threadEventsApiClient } from '../../../lib/api/thread-events.client.js'
import { useThreadEvents } from './useThreadEvents'
import { useResolvedThreadAppAuth } from '@/hooks/useResolvedThreadAppAuth.js'

/**
 * Truncates text to a maximum length and adds ellipsis if needed
 */
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength)}...`
}

interface UseChatDataReturn {
  hasMore: boolean
  threads: ChatThread[]
  selectedThreadId: string | null
  selectedThreadDetails: ThreadWithDetails | null
  messages: ChatMessage[]
  threadsLoading: boolean
  messagesLoading: boolean
  loadingMore: boolean
  filter: 'all' | 'phone' | 'email'
  searchQuery: string
  searchFilters: {
    messageTypes: string[]
    directions: string[]
    dateRange?: { from?: Date; to?: Date }
    sortOption: 'asc' | 'desc' | null
  }
  searchDeeplinkEventId: string | undefined
  searchResultSnippets: Map<string, string>
  searchResultMetadata: Map<
    string,
    {
      originalThreadId: string
      sessionId: string
      eventTime: Date
      eventType: string
      subject: string | null
      body: string | null
      summary: string | null
      transcript: string | null
    }
  >
  isThreadActive: boolean
  setSelectedThread: (threadId: string) => void
  setFilter: (filter: 'all' | 'phone' | 'email') => void
  setSearchQuery: (query: string) => void
  setSearchFilters: (filters: {
    messageTypes: string[]
    directions: string[]
    dateRange?: { from?: Date; to?: Date }
    sortOption: 'asc' | 'desc' | null
  }) => void
  loadMoreMessagesForThread: () => Promise<void>
  loadEventsAroundMessage: (eventId: string) => Promise<void>
  loadEventsAroundSession: (sessionId: string) => Promise<string>
  hasAttemptedLoad: boolean
  // Newer messages support (for deeplinks)
  hasMoreNewer: boolean
  nextCursor: string | null
  isDeeplinked: boolean
  loadMoreNewerMessages: () => Promise<void>
  // Session deeplink scroll target (resolved from sessionId)
  sessionScrollTarget: string | null
}

export const useChatData = (
  initialThreadId?: string,
  sessionId?: string,
): UseChatDataReturn => {
  const {
    isSuperAdmin,
    currentCompany,
    isCompanyLoading,
    deeplinkThreadCompanyId,
  } = useResolvedThreadAppAuth({ threadId: initialThreadId })
  const [threads, setThreads] = useState<ChatThread[]>([])
  const [allThreads, setAllThreads] = useState<ChatThread[]>([])
  const [selectedSearchResultId, setSelectedSearchResultId] = useState<
    string | null
  >(initialThreadId || null)
  const selectedThreadId = selectedSearchResultId
    ? extractThreadId(selectedSearchResultId)
    : null
  const [selectedThreadDetails, setSelectedThreadDetails] =
    useState<ThreadWithDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false)
  const [filter, setFilterState] = useState<'all' | 'phone' | 'email'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchFilters, setSearchFilters] = useState<{
    messageTypes: string[]
    directions: string[]
    dateRange?: { from?: Date; to?: Date }
    sortOption: 'asc' | 'desc' | null
  }>({ messageTypes: [], directions: [], sortOption: null })
  const [_searchResultThreadIds, setSearchResultThreadIds] = useState<
    Set<string>
  >(new Set())
  const [searchResultEventIds, setSearchResultEventIds] = useState<
    Map<string, string>
  >(new Map()) // compositeId -> eventId mapping for search results
  const [searchResultSnippets, setSearchResultSnippets] = useState<
    Map<string, string>
  >(new Map()) // compositeId -> matching snippet
  const [searchResultMetadata, setSearchResultMetadata] = useState<
    Map<
      string,
      {
        originalThreadId: string
        sessionId: string
        eventTime: Date
        eventType: string
        subject: string | null
        body: string | null
        summary: string | null
        transcript: string | null
      }
    >
  >(new Map()) // compositeId -> metadata for extracting original IDs, event time, and match detection
  const [searchDeeplinkEventId, setSearchDeeplinkEventId] = useState<
    string | undefined
  >(undefined)
  const [isThreadActive, setIsThreadActive] = useState(false) // Track if thread is actively selected (clicked) vs background

  // Track previous initialThreadId to only sync when it actually changes (not when selectedThreadId changes)
  const prevInitialThreadIdRef = useRef<string | undefined>(initialThreadId)

  // Sync selectedSearchResultId when initialThreadId changes (e.g., from URL params)
  // Only sync when initialThreadId actually changes, not when selectedSearchResultId changes
  // This allows users to switch threads after using a deeplink
  useEffect(() => {
    // Only update if initialThreadId has actually changed
    if (initialThreadId !== prevInitialThreadIdRef.current) {
      prevInitialThreadIdRef.current = initialThreadId

      // Only set if we have a new initialThreadId and it's different from current selection
      if (initialThreadId && initialThreadId !== selectedSearchResultId) {
        setSelectedSearchResultId(initialThreadId)
      }
    }
  }, [initialThreadId, selectedSearchResultId])

  // Use real thread events instead of mock data
  // selectedThreadId is always the actual thread ID (extracted from composite if needed)
  const {
    messages,
    hasMore,
    loading: eventsLoading,
    loadingMore,
    loadMoreMessages: loadMoreMessagesForThread,
    loadEventsAroundMessage,
    loadEventsAroundSession,
    hasMoreNewer,
    nextCursor,
    isDeeplinked,
    loadMoreNewerMessages,
    sessionScrollTarget,
  } = useThreadEvents(isCompanyLoading ? null : selectedThreadId, sessionId)

  // Load threads for the current or overridden company
  useEffect(() => {
    if (isCompanyLoading) {
      setLoading(true)
      return
    }

    const loadThreads = async () => {
      setLoading(true)
      setHasAttemptedLoad(true)

      // Helper to convert API response to ChatThread[]
      const convertToChatThreads = (
        data: ThreadWithDetails[],
      ): ChatThread[] => {
        return data.map(thread => ({
          id: thread.id,
          contactId: thread.contactId,
          companyId: thread.companyId,
          threadType: thread.threadType,
          userId: thread.contactId,
          sessionId: `session-${thread.id}`,
          createdAt: new Date(thread.createdAt),
          updatedAt: new Date(thread.updatedAt),
          company: {
            id: thread.companyId,
            name: 'Company',
            avatar: undefined,
          },
          participant: {
            id: thread.contactId,
            name: thread.contact?.name || 'Unknown Contact',
            senderType: thread.contact?.senderType || 'unknown',
            email: thread.email,
            phone: thread.phone,
            avatar: undefined,
          },
          lastThreadEvent: thread.threadEvents?.[0] || undefined,
          unreadCount: 0,
        }))
      }

      try {
        const threadType =
          filter === 'all' ? undefined : filter === 'phone' ? 'PHONE' : 'EMAIL'

        let response

        // If we have an initialThreadId (deeplink), use getThreadsAroundThread.
        // Super admins: only when thread belongs to current company (skip if user switched).
        // Non-super admins: always use around query - they have one company, thread must be in it.
        const shouldUseAroundQuery =
          initialThreadId &&
          (isSuperAdmin
            ? deeplinkThreadCompanyId &&
              currentCompany?.id === deeplinkThreadCompanyId
            : true)
        if (shouldUseAroundQuery) {
          try {
            if (isSuperAdmin && currentCompany?.id) {
              const adminThreadsClient = getThreadsClient(true)
              response = await adminThreadsClient.getThreadsAroundThread(
                initialThreadId,
                {
                  companyId: currentCompany.id,
                  threadType,
                  includeDetails: true,
                  limit: 50,
                },
              )
            } else {
              const regularThreadsClient = getThreadsClient(false)
              response = await regularThreadsClient.getThreadsAroundThread(
                initialThreadId,
                {
                  threadType,
                  includeDetails: true,
                  limit: 50,
                },
              )
            }
          } catch (aroundError) {
            // Thread not found or error - fall back to standard query
            toast.error('Thread not found, showing recent conversations')
          }
        }

        // Fall back to standard query if no deeplink in current company or around query failed
        if (!response) {
          if (isSuperAdmin) {
            const adminThreadsClient = getThreadsClient(true)
            response = await adminThreadsClient.getThreads({
              companyId: currentCompany!.id!,
              threadType,
              includeDetails: true,
              limit: 50,
            })
          } else {
            const regularThreadsClient = getThreadsClient(false)
            response = await regularThreadsClient.getThreads({
              threadType,
              includeDetails: true,
              limit: 50,
            })
          }
        }

        const chatThreads = convertToChatThreads(response.data)

        setAllThreads(chatThreads)
        setThreads(chatThreads)

        // Validate selected thread belongs to current company (e.g. after company switch)
        const selectedThreadInNewList = selectedSearchResultId
          ? chatThreads.find(t => t.id === selectedSearchResultId)
          : null

        if (selectedSearchResultId && !selectedThreadInNewList) {
          // Thread doesn't exist in current company's threads - clear it
          setSelectedSearchResultId(null)
          setSelectedThreadDetails(null)
        }

        // Select thread: auto-select first thread when we have no valid selection in the new list.
        // This covers: (1) no selection, (2) company switch cleared selection, (3) deeplink + company
        // switch (deeplink thread not in new company, so we cleared it and need to select something).
        if (chatThreads.length > 0 && !selectedThreadInNewList) {
          setSelectedSearchResultId(chatThreads[0].id)
        }
      } catch (error) {
        toast.error('Failed to load conversations')
      } finally {
        setLoading(false)
      }
    }

    // Super admin requires company ID to load threads
    if (isSuperAdmin && !currentCompany?.id) return

    loadThreads()
  }, [
    isCompanyLoading,
    isSuperAdmin,
    currentCompany?.id,
    filter,
    initialThreadId,
    deeplinkThreadCompanyId,
  ])

  // Mark thread as read when selected and fetch thread details
  useEffect(() => {
    if (selectedSearchResultId) {
      setThreads(prev =>
        prev.map(thread =>
          thread.id === selectedSearchResultId
            ? { ...thread, unreadCount: 0 }
            : thread,
        ),
      )

      // Fetch thread details with contact information
      // selectedThreadId is always the actual thread ID (extracted from composite)
      const fetchThreadDetails = async () => {
        try {
          const threadsClient = isSuperAdmin
            ? getThreadsClient(true)
            : getThreadsClient(false)
          const threadDetails = await threadsClient.getThreadById(
            selectedThreadId!,
          )
          setSelectedThreadDetails(threadDetails)
        } catch (error) {
          setSelectedThreadDetails(null)
        }
      }

      fetchThreadDetails()
    } else {
      setSelectedThreadDetails(null)
    }
  }, [selectedSearchResultId, selectedThreadId, isSuperAdmin])

  const setSelectedThread = useCallback(
    (threadId: string) => {
      // Keep the thread ID as-is (composite or regular) for internal lookups
      setSelectedSearchResultId(threadId)
      setIsThreadActive(true)

      // If this thread has a search result event and we have an active search,
      // set the deeplink event ID to scroll to it
      const eventId = searchResultEventIds.get(threadId)
      if (eventId && searchQuery) {
        setSearchDeeplinkEventId(eventId)
        // Note: Deeplink action type is computed on-demand in Chat.tsx based on metadata + query
      } else {
        setSearchDeeplinkEventId(undefined)
      }
    },
    [searchResultEventIds, searchQuery],
  )

  const setFilter = useCallback((newFilter: 'all' | 'phone' | 'email') => {
    setFilterState(newFilter)
    // Reset selected thread when filter changes to avoid showing wrong thread
    setSelectedSearchResultId(null)
  }, [])

  // Debounced search function
  const performSearch = useDebouncedCallback(
    async (
      query: string,
      filters: {
        messageTypes: string[]
        directions: string[]
        dateRange?: { from?: Date; to?: Date }
        sortOption: 'asc' | 'desc' | null
      },
      isSuperDataAdmin: boolean,
      companyId?: string,
    ) => {
      // Check if we have any active filters (including sort)
      const hasFilters =
        filters.messageTypes.length > 0 ||
        filters.directions.length > 0 ||
        (filters.dateRange?.from && filters.dateRange?.to) ||
        filters.sortOption !== null

      // If no query and no filters, clear search results
      if (!query.trim() && !hasFilters) {
        setThreads(allThreads)
        setSearchResultThreadIds(new Set())
        setSearchResultEventIds(new Map())
        setSearchResultSnippets(new Map())
        setSearchResultMetadata(new Map())
        return
      }

      try {
        // Convert frontend filter values to backend enum values
        const channels = filters.messageTypes
          .map(type => {
            if (type === 'sms') return 'SMS' as const
            if (type === 'email') return 'EMAIL' as const
            if (type === 'call') return 'CALL' as const
            return null
          })
          .filter((v): v is 'SMS' | 'EMAIL' | 'CALL' => v !== null)
          .join(',')

        const directions = filters.directions
          .map(dir => dir.toUpperCase())
          .join(',')

        // Convert date range to ISO strings (only if both from and to are set)
        const hasDateRange = filters.dateRange?.from && filters.dateRange?.to
        const dateFrom = hasDateRange
          ? filters.dateRange!.from!.toISOString()
          : undefined
        const dateTo = hasDateRange
          ? filters.dateRange!.to!.toISOString()
          : undefined

        let searchResponse
        if (isSuperDataAdmin) {
          searchResponse = await threadEventsApiClient.searchAdminThreadEvents({
            q: query,
            companyId,
            limit: 100,
            ...(channels && { channels }),
            ...(directions && { directions }),
            ...(dateFrom && dateTo && { dateFrom, dateTo }),
            ...(filters.sortOption && { sortBy: filters.sortOption }),
          })
        } else {
          searchResponse = await threadEventsApiClient.searchThreadEvents({
            q: query,
            limit: 100,
            ...(channels && { channels }),
            ...(directions && { directions }),
            ...(dateFrom && dateTo && { dateFrom, dateTo }),
            ...(filters.sortOption && { sortBy: filters.sortOption }),
          })
        }

        // Extract session results and duplicate threads for each matching session
        // searchResponse.data is now SessionSearchGroup[] - one group per session
        // We want to show each session separately, so the same thread appears multiple times
        const threadIds = new Set<string>()
        const eventIdMap = new Map<string, string>()
        const snippetMap = new Map<string, string>()
        const metadataMap = new Map<
          string,
          {
            originalThreadId: string
            sessionId: string
            eventTime: Date
            eventType: string
            subject: string | null
            body: string | null
            summary: string | null
            transcript: string | null
          }
        >()
        const duplicatedThreads: ChatThread[] = []

        searchResponse.data.forEach(sessionGroup => {
          threadIds.add(sessionGroup.threadId)

          // Get thread from search response included threads map
          const threadData =
            searchResponse.included.threads[sessionGroup.threadId]
          if (!threadData) return

          // Create a unique ID for this session result by appending session ID
          const compositeId = createCompositeId(
            sessionGroup.threadId,
            sessionGroup.sessionId,
          )
          const mostRelevant = sessionGroup.mostRelevantEvent

          // Store metadata including event fields for match detection
          metadataMap.set(compositeId, {
            originalThreadId: sessionGroup.threadId,
            sessionId: sessionGroup.sessionId,
            eventTime: new Date(mostRelevant.eventTime),
            eventType: mostRelevant.eventType,
            subject: mostRelevant.subject,
            body: mostRelevant.body,
            summary: mostRelevant.summary,
            transcript: mostRelevant.transcript,
          })

          // Store event ID mapping for this session
          eventIdMap.set(compositeId, mostRelevant.threadEventId)

          // Extract the best matching snippet with field label
          let snippet = ''
          if (mostRelevant.subject) {
            snippet = `Subject: ${mostRelevant.subject}`
          } else if (mostRelevant.body) {
            snippet = `Body: ${truncateText(mostRelevant.body, 100)}`
          } else if (mostRelevant.summary) {
            snippet = `Summary: ${truncateText(mostRelevant.summary, 100)}`
          } else if (mostRelevant.transcript) {
            snippet = `Transcript: ${truncateText(mostRelevant.transcript, 100)}`
          }

          // Add session count if there are multiple matching events in the session
          if (sessionGroup.eventCount > 1) {
            snippet = `${snippet} (${sessionGroup.eventCount} matches)`
          }

          snippetMap.set(compositeId, snippet)

          // Convert thread search result to ChatThread with composite ID
          duplicatedThreads.push(
            threadSearchResultToChatThread(
              threadData,
              compositeId,
              sessionGroup.sessionId,
            ),
          )
        })

        setSearchResultThreadIds(threadIds)
        setSearchResultEventIds(eventIdMap)
        setSearchResultSnippets(snippetMap)
        setSearchResultMetadata(metadataMap)
        setThreads(duplicatedThreads)
      } catch (error) {
        console.error('Search failed:', error)
        toast.error('Search failed')
      }
    },
    300,
  )

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query)
      setSearchDeeplinkEventId(undefined)
      performSearch(query, searchFilters, isSuperAdmin, currentCompany?.id)
    },
    [performSearch, searchFilters, isSuperAdmin, currentCompany?.id],
  )

  const handleFiltersChange = useCallback(
    (filters: {
      messageTypes: string[]
      directions: string[]
      dateRange?: { from?: Date; to?: Date }
      sortOption: 'asc' | 'desc' | null
    }) => {
      setSearchFilters(filters)
      performSearch(searchQuery, filters, isSuperAdmin, currentCompany?.id)
    },
    [searchQuery, performSearch, isSuperAdmin, currentCompany?.id],
  )

  return {
    filter,
    hasAttemptedLoad,
    hasMore,
    hasMoreNewer,
    loadMoreMessagesForThread,
    loadEventsAroundMessage,
    loadEventsAroundSession,
    loadMoreNewerMessages,
    loadingMore,
    isDeeplinked,
    isThreadActive,
    sessionScrollTarget,
    messages,
    messagesLoading: eventsLoading,
    nextCursor,
    selectedThreadId,
    selectedThreadDetails,
    searchQuery,
    searchFilters,
    searchDeeplinkEventId,
    searchResultSnippets,
    searchResultMetadata,
    setSelectedThread,
    setFilter,
    setSearchQuery: handleSearchChange,
    setSearchFilters: handleFiltersChange,
    threads,
    threadsLoading: loading,
  }
}
